/* eslint-disable */
import { IframeOptions } from './types/iframe-options.type';
import { Subscriber } from './types/subscriber.type';

const DOMAIN = 'video.ocularsolution.com';

declare const OcularMeetExternalAPI: any;

export class VideoConference extends EventTarget {
  private api: any;
  private domain: string;
  private options: IframeOptions;
  public subscribers:  Subscriber[] = [];
  private listeners: Map<string, (...args: any[]) => void> = new Map();

  constructor(domain?: string) {
    super();
    this.domain = domain ?? DOMAIN;

    this.options = {
      width: '100%',
      height: '100%',
      parentNode: document.querySelector('#ocular-session-room'),
      lang: 'es',
      configOverwrite: {
        bosh: `https://${this.domain}/http-bind`,
        startWithVideoMuted: false,
        startWithAudioMuted: false,
        prejoinConfig: {
          enabled: false
        },
        transcription: {
          enabled: false,
          translationLanguages: ['en', 'es', 'pt', 'tr'],
          translationLanguagesHead: ['en', 'es', 'pt', 'tr'],
          useAppLanguage: true,
          preferredLanguage: 'en-US',
          autoTranscribeOnRecord: false
        },
        disableModeratorIndicator: false,
        disablePolls: false,
        disableInviteFunctions: true,
        disableTileView: true
      },
      interfaceConfigOverwrite: {
        TOOLBAR_BUTTONS: [
          // 'microphone',
          // 'camera',
          // 'desktop',
          // 'tileview',
          // 'hangup',
          // 'chat'
        ],
        TILE_VIEW_MAX_COLUMNS: 2,
        filmStripOnly: false
      }
    };
  }

  async init(customOptions?: Partial<IframeOptions>): Promise<void> {
    const mergedOptions = {
      ...this.options,
      ...customOptions,
      configOverwrite: {
        ...this.options.configOverwrite,
        ...customOptions?.configOverwrite,
      }
    };

    this.options = mergedOptions;

    await this.loadScript();
    this.initializeAPI();
    this.registerListeners();
  }

  private async loadScript(): Promise<void> {
    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = `https://${this.domain}/external_api.js`;
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('Failed to load external API script'));
      document.head.appendChild(script);
    });
  }

  private initializeAPI(): void {
    const options = {
      roomName: this.options.roomName,
      width: '100%',
      height: '100%',
      parentNode: this.options.parentNode,
      lang: this.options.lang || 'es',
      configOverwrite: this.options.configOverwrite || {},
      interfaceConfigOverwrite: this.options.interfaceConfigOverwrite || {},
      userInfo: this.options.userInfo,
    };

    this.api = new OcularMeetExternalAPI(this.domain, options);
  }

  private registerListeners(): void {
    const videoConferenceJoinedListener = () => this.dispatchEvent(new CustomEvent('videoConferenceJoined'));
    const readyToCloseListener = () => this.dispatchEvent(new CustomEvent('myConferenceReadyToClose'));
    const participantJoinedListener = (participant: any) => this.dispatchEvent(new CustomEvent('participantJoined', { detail: participant }));
    const participantLeftListener = (participant: any) => this.dispatchEvent(new CustomEvent('participantLeft', { detail: participant }));
    const audioMuteStatusChangedListener = (participant: any, isMuted: any) => this.dispatchEvent(new CustomEvent('audioMuteStatusChanged', { detail: { participant, isMuted } }));
    const videoMuteStatusChangedListener = (participant: any, isMuted: any) => this.dispatchEvent(new CustomEvent('videoMuteStatusChanged', { detail: { participant, isMuted } }));

    this.listeners.set('videoConferenceJoined', videoConferenceJoinedListener);
    this.listeners.set('readyToClose', readyToCloseListener);
    this.listeners.set('participantJoined', participantJoinedListener);
    this.listeners.set('participantLeft', participantLeftListener);
    this.listeners.set('audioMuteStatusChanged', audioMuteStatusChangedListener);
    this.listeners.set('videoMuteStatusChanged', videoMuteStatusChangedListener);

    this.api.addListener('videoConferenceJoined', videoConferenceJoinedListener);
    this.api.addListener('readyToClose', readyToCloseListener);
    this.api.addListener('participantJoined', participantJoinedListener);
    this.api.addListener('participantLeft', participantLeftListener);
    this.api.addListener('audioMuteStatusChanged', audioMuteStatusChangedListener);
    this.api.addListener('videoMuteStatusChanged', videoMuteStatusChangedListener);
  }

  public addSubscriber(event: any): void {
    const subscriber: Subscriber = {
      displayName: event?.detail?.displayName ?? null,
      id: event?.detail?.id ?? null,
      formattedDisplayName: event?.detail?.formattedDisplayName ?? null
    }

    if (this.subscribers.some(s => s.id === subscriber.id)) {
      return;
    }

    this.subscribers.push(subscriber);
  }

  public removeSubscriber(event: any): void {
    const subscriber: Subscriber = {
      displayName: event?.detail?.displayName ?? null,
      id: event?.detail?.id ?? null,
      formattedDisplayName: event?.detail?.formattedDisplayName ?? null
    }

    this.subscribers = this.subscribers.filter(s => s.id !== subscriber.id);
  }

  public getSubscribers(): Subscriber[] {
    return this.subscribers;
  }

  public resetSubscribers(): void {
    this.subscribers = [];
  }

  public toggleAudio(): void {
    this.api.executeCommand('toggleAudio');
  }

  public toggleVideo(): void {
    this.api.executeCommand('toggleVideo');
  }

  public toggleShareScreen(): void {
    this.api.executeCommand('toggleShareScreen');
  }

  public hangup(): void {
    if (!this.api) { return; }
    this.api.executeCommand('hangup');
  }

  public endConference(): void {
    if (!this.api) { return; }
    this.api.executeCommand('endConference');
  }

  public setAvatarUrl(url: string): void {
    if (!this.api) { return; }
    this.api.executeCommand('avatarUrl', url);
  }

  public setVideoQuality(quality: number): void {
    if (!this.api) { return; }
    this.api.executeCommand('setVideoQuality', quality);
  }

  public muteEveryone(kind: 'audio' | 'video'): void {
    if (!this.api) { return; }
    this.api.executeCommand('muteEveryone', kind);
  }

  public kickParticipant(participantID: string): void {
    if (!this.api) { return; }
    this.api.executeCommand('kickParticipant', participantID);
  }

  public getParticipants(): any {
    return this.api._participants;
  }

  public onCustomEvent(eventName: string, callback: (event: CustomEvent<any>) => void): void {
    this.addEventListener(eventName, (event: Event) => {
      if (event instanceof CustomEvent) {
        callback(event as CustomEvent<any>);
      }
    });
  }

  public dispose(): void {
    try {
      if (this.api) {
        this.listeners.forEach((listener, eventName) => {
          try {
            this.api.removeListener(eventName, listener);
          } catch (error) {
            console.warn(`DISPOSE: Could not remove listener for ${eventName}:`, error);
          }
        });

        this.listeners.clear();

        try {
          this.api.dispose?.();
        } catch (error) {
          console.warn('DISPOSE: Could not dispose API:', error);
        }

        this.api = null;
      }

      this.resetSubscribers();

      const parentNode = document.querySelector('#ocular-session-room');
      if (parentNode) {
        parentNode.innerHTML = '';
      }
    } catch (error) {
      console.error('DISPOSE: Error disposing VideoConference:', error);
    }
  }
}
